import { useState, useEffect } from 'react'

// Pictures
const samplePhotos = [
  {
    id: 1,
    name: 'Cyberpunk City',
    filename: 'cyberpunk_city.webp',
    src: '/wallpapers/cyberpunk_city.webp',
    thumbnail: '/wallpapers/cyberpunk_city.webp',
    size: '394 KB',
    dimensions: '1536x1024',
    type: 'WEBP'
  },
  {
    id: 2,
    name: 'City Skyline',
    filename: 'city-skyline.jpg',
    src: 'https://images.unsplash.com/photo-1480714378408-67cf0d13bc1f?w=800&h=600&fit=crop',
    thumbnail: 'https://images.unsplash.com/photo-1480714378408-67cf0d13bc1f?w=300&h=200&fit=crop',
    size: '3.1 MB',
    dimensions: '1920x1080',
    type: 'JPEG'
  },
  {
    id: 3,
    name: 'Ocean Waves',
    filename: 'ocean-waves.jpg',
    src: 'https://images.unsplash.com/photo-1505142468610-359e7d316be0?w=800&h=600&fit=crop',
    thumbnail: 'https://images.unsplash.com/photo-1505142468610-359e7d316be0?w=300&h=200&fit=crop',
    size: '1.8 MB',
    dimensions: '1920x1280',
    type: 'JPEG'
  },  {
    id: 4,
    name: 'Forest Path',
    filename: 'forest-path.jpg',
    src: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=800&h=600&fit=crop',
    thumbnail: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=300&h=200&fit=crop',
    size: '2.7 MB',
    dimensions: '1920x1280',
    type: 'JPEG'
  },
  {
    id: 5,
    name: 'Desert Sunset',
    filename: 'desert-sunset.jpg',
    src: 'https://images.unsplash.com/photo-1509316785289-025f5b846b35?w=800&h=600&fit=crop',
    thumbnail: 'https://images.unsplash.com/photo-1509316785289-025f5b846b35?w=300&h=200&fit=crop',
    size: '2.2 MB',
    dimensions: '1920x1280',
    type: 'JPEG'
  },
  {
    id: 6,
    name: 'Northern Lights',
    filename: 'northern-lights.jpg',
    src: 'https://images.unsplash.com/photo-1531366936337-7c912a4589a7?w=800&h=600&fit=crop',
    thumbnail: 'https://images.unsplash.com/photo-1531366936337-7c912a4589a7?w=300&h=200&fit=crop',
    size: '3.5 MB',
    dimensions: '1920x1080',
    type: 'JPEG'
  }
]

const PhotoViewerApp = () => {
  const [viewMode, setViewMode] = useState('grid') // 'grid', 'single', 'slideshow'
  const [selectedPhoto, setSelectedPhoto] = useState(null)
  const [zoomLevel, setZoomLevel] = useState(1)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [slideshowActive, setSlideshowActive] = useState(false)
  const [showInfo, setShowInfo] = useState(false)

  // Navigate to next photo
  const nextPhoto = () => {
    if (!selectedPhoto) return
    const currentIndex = samplePhotos.findIndex(p => p.id === selectedPhoto.id)
    const nextIndex = currentIndex < samplePhotos.length - 1 ? currentIndex + 1 : 0
    setSelectedPhoto(samplePhotos[nextIndex])
  }

  // Navigate to previous photo
  const prevPhoto = () => {
    if (!selectedPhoto) return
    const currentIndex = samplePhotos.findIndex(p => p.id === selectedPhoto.id)
    const prevIndex = currentIndex > 0 ? currentIndex - 1 : samplePhotos.length - 1
    setSelectedPhoto(samplePhotos[prevIndex])
  }

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (viewMode === 'single' && selectedPhoto) {
        switch (e.key) {
          case 'ArrowLeft':
            prevPhoto()
            break
          case 'ArrowRight':
            nextPhoto()
            break
          case '=':
          case '+':
            setZoomLevel(Math.min(3, zoomLevel + 0.25))
            break
          case '-':
            setZoomLevel(Math.max(0.25, zoomLevel - 0.25))
            break
          case '0':
            setZoomLevel(1)
            break
          case 'i':
          case 'I':
            setShowInfo(!showInfo)
            break
          case 'g':
          case 'G':
            setViewMode('grid')
            break
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [viewMode, selectedPhoto, zoomLevel, showInfo])

  return (
    <div className="h-full flex flex-col bg-gradient-to-br from-purple-900/20 to-pink-900/20 text-white">
      {/* App Header */}
      <div className="flex items-center justify-between p-4 bg-white/10 backdrop-blur-sm border-b border-white/20">
        <div className="flex items-center space-x-3">
          <i className="fas fa-images text-pink-400 text-xl"></i>
          <h2 className="text-lg font-semibold">Photos</h2>
          <span className="text-sm text-gray-300">({samplePhotos.length} images)</span>
        </div>
        
        <div className="flex items-center space-x-2">
          {/* View Mode Buttons */}
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2 rounded-lg transition-colors ${
              viewMode === 'grid' 
                ? 'bg-pink-500/30 text-pink-300' 
                : 'hover:bg-white/10 text-gray-300'
            }`}
            title="Grid View"
          >
            <i className="fas fa-th"></i>
          </button>
          
          {selectedPhoto && (
            <>
              <button
                onClick={() => setViewMode('single')}
                className={`p-2 rounded-lg transition-colors ${
                  viewMode === 'single' 
                    ? 'bg-pink-500/30 text-pink-300' 
                    : 'hover:bg-white/10 text-gray-300'
                }`}
                title="Single View"
              >
                <i className="fas fa-image"></i>
              </button>
              
              <button
                onClick={() => setViewMode('slideshow')}
                className={`p-2 rounded-lg transition-colors ${
                  viewMode === 'slideshow' 
                    ? 'bg-pink-500/30 text-pink-300' 
                    : 'hover:bg-white/10 text-gray-300'
                }`}
                title="Slideshow"
              >
                <i className="fas fa-play"></i>
              </button>
            </>
          )}
          
          <div className="w-px h-6 bg-white/20 mx-2"></div>
          
          <button
            onClick={() => setShowInfo(!showInfo)}
            className={`p-2 rounded-lg transition-colors ${
              showInfo 
                ? 'bg-blue-500/30 text-blue-300' 
                : 'hover:bg-white/10 text-gray-300'
            }`}
            title="Info Panel"
          >
            <i className="fas fa-info-circle"></i>
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 relative overflow-hidden">
        {viewMode === 'grid' && (
          <div className="h-full p-4">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 h-full overflow-y-auto">
              {samplePhotos.map((photo) => (
                <div
                  key={photo.id}
                  className="group relative aspect-square bg-white/5 rounded-xl overflow-hidden cursor-pointer hover:ring-2 hover:ring-pink-400/50 transition-all duration-300 hover:scale-105"
                  onClick={() => {
                    setSelectedPhoto(photo)
                    setViewMode('single')
                  }}
                >
                  <img
                    src={photo.thumbnail}
                    alt={photo.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute bottom-3 left-3 right-3">
                      <p className="text-sm font-medium text-white truncate">{photo.name}</p>
                      <p className="text-xs text-gray-300">{photo.size}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Single Photo View - Enhanced with navigation and zoom */}
        {viewMode === 'single' && selectedPhoto && (
          <div className="h-full relative">
            {/* Image Display Area */}
            <div className="h-full flex items-center justify-center p-4 relative overflow-hidden">
              <img
                src={selectedPhoto.src}
                alt={selectedPhoto.name}
                className="max-w-full max-h-full object-contain rounded-lg shadow-2xl transition-transform duration-200"
                style={{ transform: `scale(${zoomLevel})` }}
              />
            </div>
            
            {/* Navigation Controls */}
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex items-center space-x-4 bg-black/50 backdrop-blur-sm rounded-2xl px-6 py-3">
              {/* Previous Button */}
              <button
                onClick={() => {
                  const currentIndex = samplePhotos.findIndex(p => p.id === selectedPhoto.id)
                  const prevIndex = currentIndex > 0 ? currentIndex - 1 : samplePhotos.length - 1
                  setSelectedPhoto(samplePhotos[prevIndex])
                }}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors text-white"
                title="Previous Photo"
              >
                <i className="fas fa-chevron-left"></i>
              </button>
              
              {/* Photo Counter */}
              <span className="text-sm text-gray-300 px-2">
                {samplePhotos.findIndex(p => p.id === selectedPhoto.id) + 1} / {samplePhotos.length}
              </span>
              
              {/* Next Button */}
              <button
                onClick={() => {
                  const currentIndex = samplePhotos.findIndex(p => p.id === selectedPhoto.id)
                  const nextIndex = currentIndex < samplePhotos.length - 1 ? currentIndex + 1 : 0
                  setSelectedPhoto(samplePhotos[nextIndex])
                }}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors text-white"
                title="Next Photo"
              >
                <i className="fas fa-chevron-right"></i>
              </button>
              
              <div className="w-px h-6 bg-white/20 mx-2"></div>
              
              {/* Zoom Controls */}
              <button
                onClick={() => setZoomLevel(Math.max(0.25, zoomLevel - 0.25))}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors text-white"
                title="Zoom Out"
                disabled={zoomLevel <= 0.25}
              >
                <i className="fas fa-minus"></i>
              </button>
              
              <span className="text-sm text-gray-300 px-2 min-w-[4rem] text-center">
                {Math.round(zoomLevel * 100)}%
              </span>
              
              <button
                onClick={() => setZoomLevel(Math.min(3, zoomLevel + 0.25))}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors text-white"
                title="Zoom In"
                disabled={zoomLevel >= 3}
              >
                <i className="fas fa-plus"></i>
              </button>
              
              <button
                onClick={() => setZoomLevel(1)}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors text-white"
                title="Fit to Window"
              >
                <i className="fas fa-expand-arrows-alt"></i>
              </button>
            </div>
          </div>
        )}
        
        {/* Slideshow View - Implementation coming in advanced features */}
        {viewMode === 'slideshow' && (
          <div className="h-full flex items-center justify-center bg-black/50">
            <p className="text-gray-300">Slideshow mode coming soon...</p>
          </div>
        )}
      </div>
      
      {/* Info Panel */}
      {showInfo && selectedPhoto && (
        <div className="absolute top-20 right-4 w-72 bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/20">
          <h3 className="text-lg font-semibold mb-3 text-pink-300">Photo Info</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-300">Name:</span>
              <span className="text-white">{selectedPhoto.name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-300">File:</span>
              <span className="text-white">{selectedPhoto.filename}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-300">Size:</span>
              <span className="text-white">{selectedPhoto.size}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-300">Dimensions:</span>
              <span className="text-white">{selectedPhoto.dimensions}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-300">Date:</span>
              <span className="text-white">{selectedPhoto.date}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-300">Type:</span>
              <span className="text-white">{selectedPhoto.type}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default PhotoViewerApp